/**
 * Frontend Service Provider for Gateway Communication
 *
 * This service handles all communication between the frontend and the Gateway service.
 *
 * NOTE: This app is moving to a multi-tenant model where EVERY call is tenant-scoped via
 * `?domain=<tenant.domain>` and where secrets must never be exposed to the browser.
 */

import axios from 'axios';
import {
  getTenantDomain,
  setTenantData as persistTenantData,
  getTenantData as readPersistedTenantData,
  clearTenantData as clearPersistedTenantData,
  setLogoutRedirectUri as persistLogoutRedirectUri,
  getLogoutRedirectUri as readLogoutRedirectUri,
  clearTenantContext as clearPersistedTenantContext,
} from '../lib/tenantContext';
import { DEFAULT_CHAT_UPLOAD_LIMITS } from '../lib/uploadLimits';
import { FALLBACK_REPORT_REASONS, normalizeReportReasons } from '../lib/reportReasons';

/**
 * Chat attachment limits — client-side preview gate. The authoritative
 * limits live server-side in `config/media.php` (limits.chat_*) and are
 * surfaced per tenant through the `/tenant/resolve` `limits` contract
 * (PG-M5). Live consumers should call `getChatUploadLimits()` from
 * `lib/uploadLimits` for the tenant-resolved values; this export remains
 * as the static fallback alias for backwards compatibility.
 */
export const CHAT_ATTACHMENT_LIMITS = DEFAULT_CHAT_UPLOAD_LIMITS;

class FrontendService {
  constructor() {
    // In browser we want same-origin calls via Kong so:
    // - no mixed-content issues on https
    // - no CORS issues
    // - Kong can inject server-side secrets for Gateway auth
    const envBaseURL = process.env.NEXT_PUBLIC_API_BASE_URL;

    let baseURL = envBaseURL;
    if (typeof window !== 'undefined') {
      // Prefer same-origin Gateway prefix when running on https.
      // (If env points to http://..., browsers will block it as mixed content.)
      const isHttps = window.location?.protocol === 'https:';
      if (!baseURL || (isHttps && String(baseURL).startsWith('http://'))) {
        baseURL = '/gateway/api/v1';
      }
    }

    this.client = axios.create({
      baseURL,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Cache for tenant and user data
    this.cache = new Map();
    this.cacheTimeout = 30 * 60 * 1000; // 30 minutes

    // Set to true for the duration of an intentional logout so the response
    // interceptor treats the expected post-revocation 401s quietly (no
    // /login redirect, no console noise). Covers both the revoke call itself
    // and any in-flight authenticated requests (chat/token, ws-token, /me)
    // from a surface that is being torn down.
    this.isLoggingOut = false;

    // Setup interceptors
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /* ====================================================================
   * Core Configuration & Setup
   * ==================================================================== */

  setupRequestInterceptor() {
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available (allow caller to override)
        const token = this.getAuthToken();
        if (token && !config.headers?.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add request ID for tracking
        config.headers['X-Request-ID'] = this.generateRequestId();

        // Enforce tenant scoping by default.
        // If caller already set params.domain, do not override.
        const existingDomain = config?.params?.domain;
        if (!existingDomain) {
          const tenantDomain = getTenantDomain();
          if (tenantDomain) {
            config.params = {
              ...(config.params || {}),
              domain: tenantDomain,
            };
          }
        }

        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  setupResponseInterceptor() {
    this.client.interceptors.response.use(
      (response) => {
        // Validate standard Gateway response format
        if (response.data && typeof response.data === 'object') {
          const { status, message, data, tenant, meta } = response.data;
          
          if (status !== undefined) {
            // Standard Gateway response - process it
            if (!status && message) {
              throw new FrontendServiceError(message, response.data, response.status);
            }
          }
        }
        
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // During an intentional logout the session token is (being) revoked,
          // so any 401 here is expected: the revoke call itself, plus any
          // in-flight authenticated requests from a surface being torn down.
          // Stay quiet — do not redirect to /login and do not clear again.
          if (this.isLoggingOut) {
            return Promise.reject(error);
          }
          // Only clear the session when the 401 came from a request that used
          // the main session token. Requests made with override tokens (masquerade
          // JWTs, etc.) must NOT invalidate the primary auth session on failure.
          const storedToken = this.getAuthToken();
          const requestAuth = error.config?.headers?.Authorization || '';
          const usedToken = requestAuth.startsWith('Bearer ')
            ? requestAuth.slice(7)
            : requestAuth;
          if (!storedToken || usedToken === storedToken) {
            this.handleUnauthorized();
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /* ====================================================================
   * Authentication & Token Management  
   * ==================================================================== */

  async login(email, password) {
    try {
      const response = await this.client.post('/login', { email, password });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Login failed', response.data);
      }

      const { token, user } = response.data.data;

      // Store authentication data
      this.setAuthToken(token);
      this.setUserData(user);

      // Clear cache on login to ensure fresh data
      this.clearCache();

      return { token, user, tenant: response.data.tenant };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Exchange an opaque SSO token for a Sanctum token + user session.
   * Requires tenant context to be resolved by Gateway (via ?domain=...).
   */
  async exchangeSsoToken(token, { domain } = {}) {
    try {
      const response = await this.client.post(
        '/sso/exchange',
        { token },
        { params: domain ? { domain } : undefined }
      );

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'SSO exchange failed', response.data);
      }

      const { token: authToken, user } = response.data.data;

      this.setAuthToken(authToken);
      this.setUserData(user);

      // Some endpoints may also include tenant info at top-level.
      if (response.data.tenant) {
        this.setTenantData(response.data.tenant);
      }

      // Clear cache to ensure fresh data under the new session
      this.clearCache();

      return { token: authToken, user, tenant: response.data.tenant || null };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async logout() {
    // Mark logout in progress so the response interceptor stays quiet on the
    // expected 401s (see the constructor + interceptor for rationale). This
    // also covers the tenant-initiated flow where the tenant routes the
    // browser through the standalone /logout (?next=<IdP logout>): the token
    // here may already be gone, so the revoke would 401 for nothing.
    this.isLoggingOut = true;

    try {
      // Only call the gateway when we actually hold a token. When there is no
      // session to revoke (e.g. tenant-initiated logout passing through the
      // standalone /logout), skip the POST so it cannot 401 needlessly.
      if (this.getAuthToken()) {
        await this.client.post('/logout');
      }
    } catch (_) {
      // Best-effort: a failed revoke is non-critical. The finally below clears
      // all local state regardless, and the user is logging out anyway.
    } finally {
      this.clearAuthToken();
      this.clearUserData();
      clearPersistedTenantContext();
      clearPersistedTenantData();
      this.clearCache();
    }
  }

  async getCurrentUser() {
    const cacheKey = 'current_user';
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const response = await this.client.get('/me');
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get user', response.data);
      }
      
      const user = response.data.data.user;
      this.setInCache(cacheKey, user);
      
      return user;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Tenant Resolution & Management
   * ==================================================================== */

  async resolveTenant(domain = null) {
    const cacheKey = `tenant_${domain || 'current'}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const params = domain ? { domain } : {};
      const response = await this.client.get('/tenant/resolve', { params });
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to resolve tenant', response.data);
      }
      
      const tenant = response.data.data.tenant;
      
      // Cache tenant data
      this.setInCache(cacheKey, tenant);
      this.setTenantData(tenant);
      
      return tenant;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Chat & Conversations
   * ==================================================================== */

  /**
   * Search the cross-tenant chat directory.
   *
   * @param {object} opts
   * @param {string} [opts.q]      Free-text query (matched against name / email).
   * @param {number} [opts.limit]  Row cap (server enforces 1..100).
   * @param {('customers'|'providers')} [opts.target]
   *   Strict per-surface role filter. Pass `'customers'` from the /escort
   *   side to get only customer-role rows, `'providers'` from the /chat
   *   side to get only escort/performer/office rows. Omitting it keeps
   *   the legacy viewer-auto-detect behaviour, which is unreliable for
   *   accounts that hold multiple roles — always pass `target` from new
   *   surfaces.
   */
  async searchChatProviders({ q = '', limit = 50, target = null } = {}) {
    try {
      const params = {
        limit,
      };

      if (q && String(q).trim()) {
        params.q = String(q).trim();
      }

      if (target === 'customers' || target === 'providers') {
        params.target = target;
      }

      // Role filtering is enforced server-side based on the authenticated
      // user's role + the explicit `target` hint above.
      const response = await this.client.get('/chat/providers', { params });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch chat providers', response.data);
      }

      return response.data.data.providers || [];
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getConversations() {
    const cacheKey = 'conversations';
    const cached = this.getFromCache(cacheKey, 5 * 60 * 1000); // 5 minutes cache
    if (cached) return cached;

    try {
      const response = await this.client.get('/conversations');
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get conversations', response.data);
      }
      
      const conversations = response.data.data.conversations || [];
      this.setInCache(cacheKey, conversations);
      
      return conversations;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async createConversation(participantId) {
    try {
      const response = await this.client.post('/conversations', {
        participant_id: participantId
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to create conversation', response.data);
      }

      // Clear conversations cache since we added a new one
      this.removeFromCache('conversations');

      return response.data.data.conversation;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Operator Greet flow: create (or fetch the existing) conversation between
   * a customer and one of the operator's ASSIGNED escorts.
   *
   * POST /conversations { participant_id, acting_as_escort_id }
   *
   * The gateway resolves the (customer, escort) pair from acting_as_escort_id
   * — the operator is never a participant themselves. Requires an operator
   * token (passed explicitly to override the localStorage interceptor) and an
   * escort the operator is assigned to (403 otherwise). Idempotent: an
   * existing thread for the pair is returned instead of a duplicate.
   */
  async createConversationAs(authToken, participantId, actingAsEscortId) {
    try {
      const response = await this.client.post(
        '/conversations',
        {
          participant_id: participantId,
          acting_as_escort_id: actingAsEscortId,
        },
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to create conversation', response.data);
      }

      // Clear conversations cache since a new thread may have been created.
      this.removeFromCache('conversations');

      return response.data.data.conversation;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Public discovery endpoint backing the chat-sidebar Discover tab.
   *
   * Wraps `GET /api/v1/listings` (gateway ListingController). The contract is
   * shared with the public listings page on tippelstreet.com so we forward
   * params verbatim — only `null` / `undefined` / empty-string entries are
   * stripped to keep the URL tidy.
   *
   * Common params:
   *   page, per_page, view ('list' | 'grid' | 'map')
   *   lat, lng, radius_km                       (center+radius)
   *   sw_lat, sw_lng, ne_lat, ne_lng            (bounding box, map view)
   *   q, type, services, ethnicities, ...       (rich filter set)
   *
   * Returns the raw `data` envelope: `{ items, pagination }`.
   */
  async getListings(params = {}) {
    // Coerce booleans to 1 / 0 because axios serialises JS `true` as the
    // string "true" in query strings, but Laravel's `boolean` validation
    // only accepts 1 / 0 / "1" / "0" / true / false (NOT "true" / "false").
    // Sending `have_photos=true` therefore returns 422 Validation failed,
    // even though the field is documented as boolean. Normalising here
    // keeps every future caller honest without shifting the burden onto
    // them.
    const cleanParams = {};
    for (const [key, value] of Object.entries(params || {})) {
      if (value === undefined || value === null) continue;
      if (typeof value === 'string' && value.trim() === '') continue;
      if (typeof value === 'boolean') {
        cleanParams[key] = value ? 1 : 0;
        continue;
      }
      cleanParams[key] = value;
    }

    try {
      const response = await this.client.get('/listings', { params: cleanParams });

      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to load listings',
          response.data
        );
      }

      return response.data.data || { items: [], pagination: {} };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async updateConversation(conversationId, patch) {
    try {
      const response = await this.client.patch(`/conversations/${conversationId}`, patch);

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to update conversation', response.data);
      }

      // Clear conversations cache since an item may have changed
      this.removeFromCache('conversations');

      return response.data.data.conversation;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Helper used by UI to match a provider's identity (LiveKit participant identity)
   * to an escort/performer ID.
   */
  async findProviderIdByIdentity(identity, role = 'escort') {
    if (!identity) return null;

    const endpoint = role === 'performer' ? '/performers' : '/escorts';

    try {
      const response = await this.client.get(endpoint, {
        params: { identity }
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to lookup provider', response.data);
      }

      const payload = response.data.data || {};
      const providers = payload.escorts || payload.performers || [];
      const match = providers.find((p) => p.identity === identity || p.name === identity);
      return match?.id || null;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getMessages(conversationId, limit = 50, offset = 0) {
    try {
      const response = await this.client.get(`/conversations/${conversationId}/messages`, {
        params: { limit, offset }
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get messages', response.data);
      }

      return (response.data.data.messages || []).reverse(); // Newest first in API, oldest first in UI
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getMessagesAs(authToken, conversationId, limit = 50, offset = 0) {
    try {
      const response = await this.client.get(`/conversations/${conversationId}/messages`, {
        params: { limit, offset },
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get messages', response.data);
      }

      return (response.data.data.messages || []).reverse();
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Upload a chat attachment (photo or video) and receive a CDN URL.
   * The returned URL is what we then pass through `attachments` on
   * sendMessage(); the gateway already accepts a URL array and stores it
   * on the message row (see StoreMessageRequest).
   *
   * Limits and rate-limits are enforced server-side
   * (config/media.php → limits.chat_*, RateLimiter::for('chat-uploads')).
   * The constants below mirror the production defaults so the FE can
   * reject obviously oversized files before the upload begins, giving
   * the user instant feedback instead of a 422 round-trip.
   *
   * @param {File} file
   * @param {'photo'|'video'} kind
   * @param {string} conversationId
   * @param {(percent:number) => void} [onProgress]
   * @returns {Promise<{ url:string, kind:string, bytes:number, mime:string, asset_uuid:string, cdn_host:string }>}
   */
  async uploadChatAttachment(file, kind, conversationId, onProgress) {
    if (!file)            throw new FrontendServiceError('No file selected', null, 400);
    if (!conversationId)  throw new FrontendServiceError('Missing conversation_id', null, 400);

    const fd = new FormData();
    fd.append('file', file);
    fd.append('conversation_id', conversationId);
    if (kind === 'photo' || kind === 'video') fd.append('kind', kind);

    try {
      const response = await this.client.post('/me/chat-attachments', fd, {
        // Let the browser set the multipart boundary; do not override
        // Content-Type here. Axios reads `Content-Type` set on the request
        // config and will leave it alone when undefined.
        headers: { 'Content-Type': undefined },
        onUploadProgress: (e) => {
          if (typeof onProgress !== 'function' || !e?.total) return;
          onProgress(Math.round((e.loaded / e.total) * 100));
        },
        // Uploads can take longer than the default 15 s; allow 2 min for
         // a 25 MB video on a slow uplink.
        timeout: 120_000,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to upload attachment',
          response.data
        );
      }
      return response.data.data.attachment;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * List the authenticated user's entity links (owner/team_member rows).
   * Powers entity-uuid discovery for provider self-service features
   * (reel recording, current-location updates) on the /escort surface.
   *
   * Cached for 60 s — entity links change rarely (onboarding/admin only)
   * but several panels may ask for them in quick succession.
   *
   * @returns {Promise<Array<{entity_uuid:string, role:string, is_active:boolean, meta:object}>>}
   */
  async getMyEntities() {
    const cacheKey = 'my_entities';
    const cached = this.getFromCache(cacheKey, 60_000);
    if (cached) return cached;

    try {
      const response = await this.client.get('/me/entities');

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to load entities', response.data);
      }

      const items = response.data.data.items || [];
      this.setInCache(cacheKey, items, 60_000);
      return items;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * The escort's primary entity UUID: the first ACTIVE link, preferring an
   * `owner` row over `team_member` (mirrors the gateway's
   * UserFollowResolver::viewerEntityUuids precedence). Returns null when the
   * user has no active entity in this tenant (customers, operators).
   *
   * @returns {Promise<string|null>}
   */
  async getMyPrimaryEntityUuid() {
    const items = await this.getMyEntities();
    const active = (Array.isArray(items) ? items : []).filter(
      (row) => row?.is_active && typeof row?.entity_uuid === 'string' && row.entity_uuid !== ''
    );
    if (active.length === 0) return null;
    const owner = active.find((row) => row.role === 'owner');
    return (owner || active[0]).entity_uuid;
  }

  /**
   * List the location rows of one of the user's own entities.
   * GET /me/entities/{uuid}/locations — the Gateway proxies this verbatim
   * to the CMS (entity_location; scope=current|coverage). The response
   * shape is CMS-owned, so callers should normalise defensively.
   *
   * @param {string} entityUuid
   * @returns {Promise<object>} Raw CMS `data` payload.
   */
  async getMyEntityLocations(entityUuid) {
    if (!entityUuid) throw new FrontendServiceError('Missing entity_uuid', null, 400);
    try {
      const response = await this.client.get(`/me/entities/${entityUuid}/locations`);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to load locations', response.data);
      }
      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Store a location row on one of the user's own entities.
   * POST /me/entities/{uuid}/locations — same payload contract as the
   * tippelstreet.com account UI (ProfileLocationController):
   *   { scope:'current', is_primary, display_order,
   *     meta:{ radius_km, expires_at, set_at }, geo:{ country_code,
   *     admin_slug, admin_name, city_slug, city_name, lat, lng } }
   * The CMS replaces the active scope=current entry, which immediately
   * drives tippelstreet.com listings and the reel geo-snapshot.
   *
   * @param {string} entityUuid
   * @param {object} payload
   * @returns {Promise<object>} Raw CMS `data` payload.
   */
  async storeMyEntityLocation(entityUuid, payload) {
    if (!entityUuid) throw new FrontendServiceError('Missing entity_uuid', null, 400);
    try {
      const response = await this.client.post(`/me/entities/${entityUuid}/locations`, payload);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to save location', response.data);
      }
      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Search cities. GET /geo/cities?q=&country= — public endpoint.
   * IMPORTANT: on country tenants (seo_mode=iso2) the country is implied
   * by the tenant, but on worldwide tenants (seo_mode=ww — e.g.
   * TippelStreet) the gateway REQUIRES an explicit `country` param and
   * 422s ("Country required") without one. Callers on ww tenants must
   * pass `country` — see EscortLocationRow's fallback-country strategy
   * (mirrors the tippelstreet.com location-search JS).
   * Items carry everything the location payload needs (slug, name, lat,
   * lng, country_code, admin_slug, admin_name).
   *
   * @param {string} q
   * @param {{ limit?: number, country?: string, includeCounties?: boolean }} [opts]
   * @returns {Promise<Array<object>>}
   */
  async searchGeoCities(q, { limit = 8, country = null, includeCounties = false } = {}) {
    const query = String(q ?? '').trim();
    if (query === '') return [];
    try {
      // include_counties=0 by default: for NL (adm2) the gateway otherwise
      // appends gemeente rows (geo_level 'county', lat/lng null) that render
      // as duplicates of their namesake city and cannot be saved as a
      // current location (the CMS store requires numeric lat/lng).
      const params = { q: query, limit, include_counties: includeCounties ? 1 : 0 };
      if (typeof country === 'string' && country.trim() !== '') {
        params.country = country.trim().toLowerCase();
      }
      const response = await this.client.get('/geo/cities', { params });
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'City search failed', response.data);
      }
      return response.data.data.items || [];
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Resolve the nearest known city for a coordinate pair (browser
   * geolocation flow). GET /geo/nearest — returns the same city item
   * shape as searchGeoCities, or null when nothing matched.
   *
   * @param {number} lat
   * @param {number} lng
   * @returns {Promise<object|null>}
   */
  async geoNearestCity(lat, lng) {
    try {
      const response = await this.client.get('/geo/nearest', {
        params: { lat, lng },
      });
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Nearest-city lookup failed', response.data);
      }
      return response.data.data.city || null;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Upload a recorded reel to the Gateway encoding pipeline.
   *
   * POST /me/reels/upload — the SAME pipeline the tippelstreet.com
   * My Media → Reels tab uses: probe (min 3 s / max 300 s) → encode →
   * verifypass-21 auto-approval for verified creators → timeline post via
   * CreateReelTimelinePostJob. Uploading here therefore also creates the
   * /timeline post once the reel is approved; no extra calls needed.
   *
   * @param {string} entityUuid  The creator's entity (must be owner/manager).
   * @param {File}   file        Recorded video (mp4/webm/quicktime).
   * @param {{ title?: string, onProgress?: (percent:number) => void }} [opts]
   * @returns {Promise<{ reel_uuid:string, status:string }>}
   */
  async uploadReel(entityUuid, file, { title, onProgress } = {}) {
    if (!entityUuid) throw new FrontendServiceError('Missing entity_uuid', null, 400);
    if (!file)       throw new FrontendServiceError('No recording to upload', null, 400);

    const fd = new FormData();
    fd.append('entity_uuid', String(entityUuid));
    fd.append('file', file);
    if (typeof title === 'string' && title.trim() !== '') {
      fd.append('title', title.trim());
    }

    try {
      const response = await this.client.post('/me/reels/upload', fd, {
        // Let the browser set the multipart boundary (same pattern as
        // uploadChatAttachment).
        headers: { 'Content-Type': undefined },
        onUploadProgress: (e) => {
          if (typeof onProgress !== 'function' || !e?.total) return;
          onProgress(Math.round((e.loaded / e.total) * 100));
        },
        // The gateway allows up to 300 s for reel ingestion; a 15–30 s
        // webm is small, but slow uplinks + Bunny passthrough need slack.
        timeout: 300_000,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to upload reel',
          response.data
        );
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async sendMessage(conversationId, text, attachments = [], clientId = null) {
    try {
      const payload = {
        conversation_id: conversationId,
        text: text.trim(),
        attachments
      };

      if (clientId) {
        payload.client_id = clientId;
      }

      const response = await this.client.post('/messages', payload);

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to send message', response.data);
      }

      return response.data.data.message;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Send a message as the authenticated operator, optionally attributing it to
   * a masqueraded escort.
   *
   * @param {string|null} actingAsEscortId - When the operator is masquerading,
   *   this is the escort's user ID. The gateway will set sender_id = escortId,
   *   operator_id = operatorId, and acting_as_escort_id = escortId on the
   *   message row so attribution is correct for billing and audit.
   *   Pass null for non-masquerade sends.
   */
  async sendMessageAs(authToken, conversationId, text, attachments = [], clientId = null, actingAsEscortId = null, originSnippetId = null) {
    try {
      const payload = {
        conversation_id: conversationId,
        text: text.trim(),
        attachments
      };

      if (clientId) {
        payload.client_id = clientId;
      }

      // Include the masquerade escort ID so the gateway sets the correct
      // sender_id and operator attribution fields on the message row.
      if (actingAsEscortId) {
        payload.acting_as_escort_id = actingAsEscortId;
      }

      // Snippet/trigger effectiveness (M2): when the draft originated from an
      // inserted snippet or trigger, pass its id so the gateway records the
      // usage (ledger + usage_count).
      if (originSnippetId) {
        payload.origin_snippet_id = originSnippetId;
      }

      const response = await this.client.post('/messages', payload, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to send message', response.data);
      }

      return response.data.data.message;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Guard for realtime (Centrifugo) token requests. Once a logout is in
   * progress the session token is being revoked, so these requests would 401
   * and surface as red errors in the Network tab. A Centrifugo connection or
   * subscription that is still tearing down can fire its getToken callback
   * after navigation has begun — short-circuit here so no doomed HTTP request
   * ever leaves the browser. Throwing (rather than returning) keeps the
   * Centrifugo getToken contract intact (it expects a token or a throw).
   */
  _ensureNotLoggingOut() {
    if (this.isLoggingOut) {
      throw new FrontendServiceError('Logout in progress; realtime token request skipped.', {}, 401);
    }
  }

  async generateChatToken() {
    this._ensureNotLoggingOut();
    try {
      const response = await this.client.post('/chat/token');

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to generate chat token', response.data);
      }

      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async generateChatSubscriptionToken(conversationId) {
    this._ensureNotLoggingOut();
    try {
      const response = await this.client.post('/chat/subscription-token', {
        conversation_id: conversationId,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to generate chat subscription token', response.data);
      }

      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  // ===================================================================
  // Earnings (role-scoped, gateway-aggregated)
  // ===================================================================

  /**
   * Role-scoped earnings summary for the authenticated user.
   * Backed by GET /me/earnings — the gateway resolves which scopes the
   * caller may see (escort / operator / office / …) and returns
   * already-aggregated, maturity-split totals. The client never recomputes
   * a split or total.
   *
   * Returns null on 403 (no earnings surface, e.g. customers) so callers
   * can render a neutral empty state without special-casing HTTP errors.
   *
   * @param {object} [params] Optional ISO date range { from, to }.
   * @returns {Promise<object|null>} The earnings envelope data or null.
   */
  async getMyEarnings(params = {}) {
    try {
      const response = await this.client.get('/me/earnings', { params });
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to load earnings',
          response.data,
        );
      }
      return response.data.data || null;
    } catch (error) {
      if (error?.response?.status === 403) {
        return null;
      }
      throw this.normalizeError(error);
    }
  }

  // ===================================================================
  // In-app notifications inbox
  // ===================================================================

  /**
   * Unread counts + by_category breakdown for the inbox badge / chips.
   * Backed by GET /me/notifications/summary. Cached 10s server-side.
   *
   * @returns {Promise<{ unread_total: number, by_category: object, by_group: object }>}
   */
  async getNotificationSummary() {
    try {
      const response = await this.client.get('/me/notifications/summary');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to load notification summary',
          response.data,
        );
      }
      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Paginated notifications feed. Filters: { unread, category, group, per_page }.
   * Backed by GET /me/notifications.
   */
  async listNotifications(params = {}) {
    try {
      const response = await this.client.get('/me/notifications', { params });
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to load notifications',
          response.data,
        );
      }
      const body = response.data.data || {};
      return {
        data: Array.isArray(body.data) ? body.data : [],
        meta: body.meta || {},
      };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Claim a bonus tied to a notification (welcome bonus, streak, win-back).
   * Backed by POST /me/notifications/{uuid}/claim. Idempotent server-side.
   *
   * @returns {Promise<{ id: string, claim_status: 'claimed', bonus_credits: number, wallet: object }>}
   */
  async claimNotificationBonus(uuid) {
    try {
      const response = await this.client.post(`/me/notifications/${encodeURIComponent(uuid)}/claim`);
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to claim bonus',
          response.data,
        );
      }
      // Bust the wallet balance cache so the FE shows the new credits.
      try { this.removeFromCache?.('wallet_balance'); } catch (_) {}
      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Mark notifications read. Payload: { ids?, category?, group?, all? }.
   * Backed by POST /me/notifications/read.
   *
   * @returns {Promise<{ updated: number }>}
   */
  async markNotificationsRead(payload = {}) {
    try {
      const response = await this.client.post('/me/notifications/read', payload);
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to mark notifications read',
          response.data,
        );
      }
      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Subscription token for the per-user Centrifugo notify channel
   * (`notify.user.{user_id}`). Used by ChatProvider to subscribe to
   * real-time inbox events.
   *
   * @returns {Promise<{ token: string, channel: string, expires_in: number }>}
   */
  async generateNotifyToken() {
    this._ensureNotLoggingOut();
    try {
      const response = await this.client.post('/me/notifications/ws-token');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to generate notify token',
          response.data,
        );
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Subscription token for the tenant-wide Centrifugo presence channel.
   * Backed by POST /chat/presence-token. Used once per WS session by
   * `ChatProvider.subscribeToTenantPresence` so the client can bind its
   * user_id into Centrifugo's presence map — which then powers the
   * Online tab + every `is_online` enrichment elsewhere.
   *
   * @returns {Promise<{ token: string, channel: string, expires_in: number }>}
   */
  async generateTenantPresenceToken() {
    this._ensureNotLoggingOut();
    try {
      const response = await this.client.post('/chat/presence-token');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to generate presence token',
          response.data,
        );
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  // ===================================================================
  // Safety: abuse reports & user restrictions (PG-M6)
  // ===================================================================

  /**
   * Report-reason options for the abuse report picker, resolved from the
   * gateway catalog registry (PG-M5) so the list can never drift from the
   * codes the `/reports` validator accepts. Tenant overrides + locale are
   * applied server-side. Falls back to the historical general-purpose set
   * when the registry is unreachable or empty — mirroring the gateway's
   * own AbuseReport::REASONS fallback. Cached for 5 minutes.
   *
   * @returns {Promise<Array<{ code: string, label: string }>>}
   */
  async getReportReasons() {
    const cacheKey = 'report_reasons';
    const cached = this.getFromCache(cacheKey, 5 * 60 * 1000);
    if (cached) return cached;

    try {
      const response = await this.client.get('/catalog/registry', {
        params: { catalogs: 'report_reasons' },
      });
      const items = response.data?.data?.catalogs?.report_reasons;
      const options = normalizeReportReasons(items);
      if (options.length === 0) {
        // Registry reachable but empty (pre-seed database) — use the
        // fallback WITHOUT caching so a later seed is picked up promptly.
        return [...FALLBACK_REPORT_REASONS];
      }
      this.setInCache(cacheKey, options);
      return options;
    } catch (_) {
      return [...FALLBACK_REPORT_REASONS];
    }
  }

  /**
   * Submit a polymorphic abuse report. Backed by POST /reports
   * (throttle 5/min). Subject pairs supported by the gateway:
   * user (numeric id as string), conversation (uuid, reporter must be a
   * participant), entity / timeline_post / timeline_comment / reel /
   * review (uuids).
   *
   * The endpoint uses a flat envelope (201 {message, report_id};
   * 409 {error} on duplicate). A duplicate pending report is returned as
   * `alreadyReported: true` instead of throwing — the UI treats it as a
   * benign "we already have this" outcome.
   *
   * @param {{ subjectType: string, subjectUuid: string|number, reason: string, details?: string|null, evidence?: object|null }} payload
   * @returns {Promise<{ alreadyReported: boolean, reportId: number|null, message: string }>}
   */
  async submitAbuseReport({ subjectType, subjectUuid, reason, details = null, evidence = null }) {
    try {
      const response = await this.client.post('/reports', {
        subject_type: subjectType,
        subject_uuid: String(subjectUuid),
        reason,
        ...(details ? { details } : {}),
        ...(evidence ? { evidence } : {}),
      });
      return {
        alreadyReported: false,
        reportId: response.data?.report_id ?? null,
        message: response.data?.message || 'Report submitted.',
      };
    } catch (error) {
      if (error?.response?.status === 409) {
        return {
          alreadyReported: true,
          reportId: null,
          message: error.response?.data?.error || 'You have already reported this. We are reviewing it.',
        };
      }
      throw this.normalizeFlatError(error);
    }
  }

  /**
   * Open an appeal for a completed moderation case. The Gateway derives the
   * appellant from the authenticated Sanctum principal; callers can never
   * submit an identity in this payload.
   *
   * @param {{ originalCaseId: string, statement: string }} payload
   * @returns {Promise<{ id: string, state: string, title?: string }>}
   */
  async submitModerationAppeal({ originalCaseId, statement }) {
    try {
      const response = await this.client.post('/me/moderation-appeals', {
        original_case_id: originalCaseId,
        statement,
      });

      return response.data?.data || {};
    } catch (error) {
      throw this.normalizeFlatError(error);
    }
  }

  /**
   * Raise a PanicLink emergency. Coordinates are optional and are encrypted
   * by the Gateway; the response never echoes location.
   *
   * @param {{ conversationId?: string|null, location?: { latitude: number, longitude: number, accuracy_meters?: number }|null, message?: string|null }} payload
   * @returns {Promise<{ id: string, status: string, created_at: string }>}
   */
  async raisePanicEvent({ conversationId = null, location = null, message = null } = {}) {
    try {
      const response = await this.client.post('/me/panic-events', {
        ...(conversationId ? { conversation_id: conversationId } : {}),
        ...(location ? { location } : {}),
        ...(message ? { context: { message } } : {}),
      });

      return response.data?.data || {};
    } catch (error) {
      throw this.normalizeFlatError(error);
    }
  }

  /**
   * Restrictions the authenticated user placed on other users (TS-M1
   * block/mute). Backed by GET /me/restrictions (flat {data} envelope).
   * Rows: { scope, muted_until, blocked, active, target: { id, … } }.
   * Cached for 60s; mutations below bust the cache.
   *
   * @returns {Promise<Array<object>>}
   */
  async listRestrictions() {
    const cacheKey = 'my_restrictions';
    const cached = this.getFromCache(cacheKey, 60 * 1000);
    if (cached) return cached;

    try {
      const response = await this.client.get('/me/restrictions');
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      this.setInCache(cacheKey, rows);
      return rows;
    } catch (error) {
      throw this.normalizeFlatError(error);
    }
  }

  /**
   * Place or refresh a restriction on another user. Backed by
   * PUT /me/restrictions/{targetUserId}.
   *
   * @param {number|string} targetUserId Gateway user id of the target.
   * @param {{ scope?: 'chat'|'comments'|'follow'|'all', mode?: 'block'|'mute', duration?: '30m'|'1h'|'3h'|'1d' }} [opts]
   *   `duration` is required by the gateway when mode is 'mute'.
   * @returns {Promise<{ scope: string, muted_until: string|null, blocked: boolean, active: boolean }>}
   */
  async restrictUser(targetUserId, { scope = 'all', mode = 'block', duration = undefined } = {}) {
    try {
      const response = await this.client.put(
        `/me/restrictions/${encodeURIComponent(String(targetUserId))}`,
        { scope, mode, ...(duration ? { duration } : {}) },
      );
      this.removeFromCache('my_restrictions');
      return response.data?.data || null;
    } catch (error) {
      throw this.normalizeFlatError(error);
    }
  }

  /**
   * Lift a restriction. Backed by DELETE /me/restrictions/{targetUserId}.
   * Without a scope every restriction on the target is lifted.
   *
   * @param {number|string} targetUserId
   * @param {string|null} [scope]
   * @returns {Promise<{ message: string }>}
   */
  async liftRestriction(targetUserId, scope = null) {
    try {
      const response = await this.client.delete(
        `/me/restrictions/${encodeURIComponent(String(targetUserId))}`,
        { params: scope ? { scope } : {} },
      );
      this.removeFromCache('my_restrictions');
      return response.data || {};
    } catch (error) {
      throw this.normalizeFlatError(error);
    }
  }

  /**
   * "Wie volgt mij" — paginated reverse view of the followers ledger.
   * Powers the escort-side Followers tab. Each row is enriched with avatar,
   * city and live presence (`is_online`) plus a `total_distinct` count
   * usable as a motivation header.
   *
   * @param {{ page?: number, perPage?: number }} [opts]
   * @returns {Promise<{ data: Array, meta: { current_page:number, last_page:number, per_page:number, total:number, total_distinct:number } }>}
   */
  async getMyFollowers({ page = 1, perPage = 25 } = {}) {
    try {
      const response = await this.client.get('/me/followers/users', {
        params: { page, per_page: perPage },
      });
      // FollowersController returns a flat envelope {data, meta} (no {status}
      // wrapper) so we just sanity-check the shape here.
      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      const meta = response.data?.meta || {};
      return { data, meta };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Tenant-wide live online users, role-filtered (customers see providers,
   * providers/office see customers). Powers the escort-side Online tab.
   *
   * @returns {Promise<{ users: Array, total: number }>}
   */
  async getOnlineUsers() {
    try {
      const response = await this.client.get('/me/online-users');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to load online users',
          response.data,
        );
      }
      const data = response.data.data || {};
      return {
        users: Array.isArray(data.users) ? data.users : [],
        total: Number.isFinite(data.total) ? data.total : 0,
      };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Send a typing indicator for a conversation.
   *
   * @param {string}      conversationId   - UUID of the conversation.
   * @param {boolean}     isTyping         - true = typing_start, false = typing_stop.
   * @param {string|null} authToken        - Explicit Sanctum token (operator flow).
   *   When null the request interceptor supplies the token from localStorage
   *   (customer / escort flow). Operators must pass their token explicitly
   *   because they are not direct conversation participants and the gateway
   *   needs acting_as_escort_id to locate the conversation.
   * @param {number|null} actingAsEscortId - Escort's user ID (operator flow).
   *   The gateway uses this to find the conversation via escort_id instead of
   *   the operator's own user ID. Mirrors the sendMessageAs pattern.
   */
  async sendChatTyping(conversationId, isTyping = true, authToken = null, actingAsEscortId = null) {
    try {
      const payload = {
        conversation_id: conversationId,
        is_typing: !!isTyping,
      };
      if (actingAsEscortId) {
        payload.acting_as_escort_id = actingAsEscortId;
      }
      const response = await this.client.post('/chat/typing', payload, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to send typing indicator', response.data);
      }

      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Operator API
   * ==================================================================== */

  async getOperatorRoster(authToken = null) {
    try {
      const response = await this.client.get('/operators/roster', {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch roster', response.data);
      }

      return response.data.data.escorts || [];
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Customer directory for the operator console Customers tab.
   * GET /operators/customers?list=online|new|returning_today|paid|recent_chats|recently_online
   *
   * Rows are assignment-scoped (only customers with conversation refs to the
   * operator's escorts appear in list-specific joins) and carry a presence
   * snapshot plus a customer_insights placeholder (filled by M3).
   *
   * @returns {Promise<{ list: string, users: Array, total: number }>}
   */
  async getOperatorCustomers(authToken = null, { list = null, limit = null } = {}) {
    try {
      const params = {};
      if (list) params.list = list;
      if (limit) params.limit = limit;
      const response = await this.client.get('/operators/customers', {
        params: Object.keys(params).length ? params : undefined,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch customers', response.data);
      }

      const data = response.data.data || {};
      return {
        list: data.list || list || 'online',
        users: Array.isArray(data.users) ? data.users : [],
        total: Number.isFinite(data.total) ? data.total : 0,
      };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Weekly operator toplist (M6 gamification).
   * GET /operators/toplist?week=YYYY-Www
   *
   * Returns the viewer-neutral gateway payload: { week, weeks, rows,
   * unranked, thresholds, computed_at, next_update_at }. The FE pins the
   * caller's own row by operator_id (lib/operatorToplist.findOwnRow).
   *
   * @param {string|null} authToken - Operator Sanctum token.
   * @param {{ week?: string|null }} options - Optional ISO week key; omit for the current week.
   * @returns {Promise<object>}
   */
  async getOperatorToplist(authToken = null, { week = null } = {}) {
    try {
      const response = await this.client.get('/operators/toplist', {
        params: week ? { week } : undefined,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch toplist', response.data);
      }

      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Tenant ops dashboard for the operator header strip (M7).
   * GET /operators/dashboard — cached ~60s per tenant on the gateway.
   *
   * @param {string|null} authToken - Operator Sanctum token.
   * @returns {Promise<object>} { customers_online, operators_online,
   *   waiting_conversations, revenue_today_cents,
   *   avg_first_response_seconds_today, next_update_at }
   */
  async getOperatorDashboard(authToken = null) {
    try {
      const response = await this.client.get('/operators/dashboard', {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch dashboard', response.data);
      }

      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getOperatorContacts(authToken = null, { escortId = null, limit = null, filter = null } = {}) {
    try {
      const params = {};
      if (escortId) params.escort_id = escortId;
      if (limit) params.limit = limit;
      if (filter) params.filter = filter;
      const response = await this.client.get('/operators/contacts', {
        params: Object.keys(params).length ? params : undefined,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch contacts', response.data);
      }

      return response.data.data.conversations || [];
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Explicitly claim a conversation on behalf of the calling operator.
   * POST /operators/conversations/{id}/claim
   * Returns the claim object including claimed_by_me, expires_at, expires_in_seconds.
   */
  async claimConversation(authToken, conversationId) {
    try {
      const response = await this.client.post(
        `/operators/conversations/${conversationId}/claim`,
        {},
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to claim conversation', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Release the calling operator's active claim on a conversation.
   * DELETE /operators/conversations/{id}/claim
   * Returns the released claim object.
   */
  async releaseConversation(authToken, conversationId) {
    try {
      const response = await this.client.delete(
        `/operators/conversations/${conversationId}/claim`,
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to release conversation', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Fetch the curated tenant profile for one assigned escort (Performer
   * context panel). GET /operators/escorts/{escortId}/profile
   * Returns the profile object (photos, bio, rates, verification, social).
   * 404 = escort has no published tenant profile; callers should render a
   * graceful fallback rather than an error.
   */
  async getOperatorEscortProfile(authToken, escortId) {
    try {
      const response = await this.client.get(`/operators/escorts/${escortId}/profile`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch escort profile', response.data);
      }
      return response.data.data.profile;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * List the location rows of an assigned escort's entity (M4).
   * GET /operators/escorts/{escortId}/locations — the gateway resolves the
   * entity from the escort user and proxies the exact CMS contract of
   * /me/entities/{uuid}/locations, so callers normalise with the same
   * extractActiveCurrentLocation helper the /escort surface uses.
   *
   * @returns {Promise<object>} Raw CMS `data` payload.
   */
  async getOperatorEscortLocations(authToken, escortId) {
    try {
      const response = await this.client.get(`/operators/escorts/${escortId}/locations`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch escort locations', response.data);
      }
      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Store a current-location for an assigned escort's entity (M4).
   * POST /operators/escorts/{escortId}/locations — same payload contract as
   * storeMyEntityLocation (buildCurrentLocationPayload shape). The gateway
   * is assignment-enforced, audit-logs the save and busts the cached
   * performer-profile payload.
   *
   * @returns {Promise<object>} Raw CMS `data` payload.
   */
  async storeOperatorEscortLocation(authToken, escortId, payload) {
    try {
      const response = await this.client.post(`/operators/escorts/${escortId}/locations`, payload, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to save escort location', response.data);
      }
      return response.data.data || {};
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async startOperatorMasquerade(authToken, escortId, ttlMinutes = 30) {
    try {
      const response = await this.client.post(
        '/operators/masquerade/start',
        { escort_id: escortId, ttl_minutes: ttlMinutes },
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to start masquerade', response.data);
      }

      return response.data.data.masquerade_token;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  // =========================================================================
  // Live-session lifecycle (request → accept / decline)
  // =========================================================================

  /**
   * Customer or operator requests a live or audio session.
   * POST /conversations/{id}/live/request
   * @param {string} conversationId
   * @param {'live'|'audio'} mode
   * @param {number|null} pricePerMin - cents per minute, optional
   */
  async requestLiveSession(conversationId, mode = 'live', pricePerMin = null) {
    try {
      const payload = { mode };
      if (pricePerMin != null) payload.price_per_min = pricePerMin;
      const response = await this.client.post(
        `/conversations/${conversationId}/live/request`,
        payload
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to request live session', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Escort accepts a pending live/audio request with an explicit mode choice.
   * POST /conversations/{id}/live/accept
   * @param {string} conversationId
   * @param {'audio_only'|'live_full_camera'|'live_no_camera'|'live_hidden_camera'|'live_filtered_camera'} mode
   * @param {{ customerMic?: boolean, customerCamera?: boolean }} [opts]
   *        Optional per-session overrides for what the customer may publish.
   *        Omitted fields fall back to the escort's persistent defaults
   *        (User::livePreferencesResolved). The server forces customerCamera
   *        to false for audio_only and live_no_camera regardless of input.
   * (escort only — Gateway enforces this; mode is validated server-side)
   */
  async acceptLiveSession(conversationId, mode, opts = undefined) {
    try {
      const payload = { mode };
      if (opts && typeof opts === 'object') {
        if (typeof opts.customerMic    === 'boolean') payload.customer_mic    = opts.customerMic;
        if (typeof opts.customerCamera === 'boolean') payload.customer_camera = opts.customerCamera;
      }
      const response = await this.client.post(
        `/conversations/${conversationId}/live/accept`,
        payload
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to accept live session', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Escort ends an active live session for all participants.
   * POST /conversations/{id}/live/end
   * Stops PPM billing atomically on the backend.
   * (escort only — Gateway enforces this)
   */
  async endLiveSession(conversationId) {
    try {
      const response = await this.client.post(`/conversations/${conversationId}/live/end`);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to end session', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Update the caller's room presence flag for an active live session.
   * POST /conversations/{id}/live/presence
   * Called when the user connects (present=true) or disconnects (present=false)
   * from the LiveKit room. Best-effort — failures do not affect billing.
   * @param {string}  conversationId
   * @param {boolean} present
   */
  async updateLiveSessionPresence(conversationId, present) {
    try {
      const response = await this.client.post(
        `/conversations/${conversationId}/live/presence`,
        { present: !!present }
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to update presence', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Fetch a short-lived LiveKit join token for the active live/audio session.
   * POST /conversations/{id}/live/join-token
   * Both escort and customer may call this once the session is accepted.
   * Returns { token, server_url, room_name, accepted_mode, role }
   */
  async getLiveSessionJoinToken(conversationId) {
    try {
      const response = await this.client.post(
        `/conversations/${conversationId}/live/join-token`
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get join token', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Heartbeat a running PPM session so the gateway monitor (`ppm:monitor`)
   * does not finalize it as 'timeout'. Touches `last_beat_at = now()` and
   * returns the current meter info.
   *
   * POST /ppm/sessions/{id}/heartbeat
   *
   * Authorization (gateway-side):
   *   Only the payer (customer) may heartbeat their own session. The escort
   *   would receive 403 — callers must therefore gate this call to the
   *   customer role on the FE side too.
   *
   * Terminal-state behaviour:
   *   - 409 "Session already charged": the gateway already finalized this
   *     session (e.g. customer hit /live/end, or insufficient balance).
   *     Treat as a non-error signal to stop polling.
   *   - 401/403: auth/role mismatch — stop polling, do not surface as an
   *     unexpected error.
   *   - Other failures (network, 5xx): bubble through normalizeError so the
   *     caller can decide whether to retry on the next tick.
   *
   * @param {string} ppmSessionId
   * @returns {Promise<{ session: object, minutes_sofar: number, amount_sofar: number }>}
   */
  async heartbeatPpmSession(ppmSessionId) {
    try {
      const response = await this.client.post(
        `/ppm/sessions/${ppmSessionId}/heartbeat`,
        {}
      );
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to heartbeat PPM session',
          response.data
        );
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Submit a WebRTC quality snapshot for the current live session. Used by
   * the WebRTCStatsSampler in LiveSessionRoom to feed the gateway's
   * `webrtc_session_stats` table for dashboarding + alerting.
   *
   * POST /webrtc/stats
   *
   * Authorization:
   *   Sanctum-authenticated user. Tenant + (optional) conversation
   *   participant check happens server-side in WebRtcStatsController.
   *
   * Failure mode:
   *   This is fire-and-forget telemetry. The endpoint MUST NEVER raise a
   *   user-visible error, so we deliberately catch everything and return
   *   `false` instead of re-throwing. The sampler will simply try again on
   *   the next 10-second tick.
   *
   * @param {{
   *   at?: number,
   *   room?: string|null,
   *   conversation_id?: string|null,
   *   role: 'escort'|'customer'|'operator',
   *   accepted_mode?: string|null,
   *   available_outgoing_bitrate?: number|null,
   *   tracks: Array<object>,
   * }} payload
   * @returns {Promise<boolean>} true on success, false otherwise.
   */
  async submitWebRtcStats(payload) {
    try {
      const response = await this.client.post('/webrtc/stats', payload);
      return !!response?.data?.status;
    } catch (_err) {
      // Swallow — telemetry must not surface to the user. The sampler
      // re-attempts on the next tick anyway, and rate-limiting (60/min)
      // is enforced server-side so we don't even need our own backoff.
      return false;
    }
  }

  // =========================================================================
  // Escort session-availability preferences
  // =========================================================================

  /**
   * Fetch the authenticated user's resolved live preferences.
   * Returns { text_chat_enabled, audio_enabled, live_enabled, allowed_live_modes }.
   */
  async getMyLivePreferences() {
    try {
      const response = await this.client.get('/me/live-preferences');
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch preferences', response.data);
      }
      return response.data.data.preferences;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Save the authenticated user's live preferences (full replacement).
   * @param {{ text_chat_enabled: boolean, audio_enabled: boolean, live_enabled: boolean, allowed_live_modes: string[] }} preferences
   */
  async updateMyLivePreferences(preferences) {
    try {
      const response = await this.client.put('/me/live-preferences', preferences);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to update preferences', response.data);
      }
      return response.data.data.preferences;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Escort declines a pending live/audio request.
   * POST /conversations/{id}/live/decline
   * (escort only — Gateway enforces this)
   */
  async declineLiveSession(conversationId) {
    try {
      const response = await this.client.post(
        `/conversations/${conversationId}/live/decline`
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to decline live session', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  // =========================================================================
  // Escort call controls (mute / block / resume / report)
  // =========================================================================
  // All escort-only — the gateway rejects anyone who is not the
  // conversation's escort. mute/block/report also auto-decline a pending
  // request server-side; state updates arrive via the live_session.declined
  // Centrifugo event like a normal decline. Every call resolves to the
  // updated `call_restriction` payload ({ muted_until, blocked, reported_at,
  // active }) so the caller can update local UI state immediately.

  /**
   * Mute call requests from this conversation's customer.
   * POST /conversations/{id}/live/mute
   * @param {string} conversationId
   * @param {'30m'|'1h'|'3h'|'until_tomorrow'} duration
   */
  async muteLiveCalls(conversationId, duration) {
    try {
      const response = await this.client.post(
        `/conversations/${conversationId}/live/mute`,
        { duration }
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to mute calls', response.data);
      }
      return response.data.data.call_restriction;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Resume calls — lifts both a temporary mute and a permanent block.
   * DELETE /conversations/{id}/live/mute
   */
  async resumeLiveCalls(conversationId) {
    try {
      const response = await this.client.delete(
        `/conversations/${conversationId}/live/mute`
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to resume calls', response.data);
      }
      return response.data.data.call_restriction;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Block call requests from this customer until explicitly resumed.
   * POST /conversations/{id}/live/block
   */
  async blockLiveCalls(conversationId) {
    try {
      const response = await this.client.post(
        `/conversations/${conversationId}/live/block`
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to block calls', response.data);
      }
      return response.data.data.call_restriction;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Flag abusive behaviour by this customer (also declines a pending
   * request server-side). Does not mute by itself.
   * POST /conversations/{id}/live/report
   */
  async reportLiveCallAbuse(conversationId) {
    try {
      const response = await this.client.post(
        `/conversations/${conversationId}/live/report`
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to submit report', response.data);
      }
      return response.data.data.call_restriction;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Customer (or operator) cancels their own pending live/audio request.
   * POST /conversations/{id}/live/cancel
   *
   * The escort path uses declineLiveSession instead. Cancel resets the
   * conversation to idle and emits a live_session.cancelled event.
   */
  async cancelLiveSession(conversationId) {
    try {
      const response = await this.client.post(
        `/conversations/${conversationId}/live/cancel`
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to cancel live session', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  // =========================================================================
  // Paid-conversation lifecycle
  // =========================================================================

  /**
   * Set a conversation to paid mode (operator / escort only).
   * Returns the updated payment-state fields from the gateway.
   */
  async setPaidConversation(conversationId) {
    try {
      const response = await this.client.post(`/conversations/${conversationId}/set-paid`);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to set paid', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Reset a conversation back to free mode (operator / escort only).
   */
  async setFreeConversation(conversationId) {
    try {
      const response = await this.client.post(`/conversations/${conversationId}/set-free`);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to set free', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Customer approves the paid transition for a conversation.
   */
  async approvePaidConversation(conversationId) {
    try {
      const response = await this.client.post(`/conversations/${conversationId}/approve-paid`);
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to approve paid mode', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async stopOperatorMasquerade(authToken, escortId) {
    try {
      const response = await this.client.post(
        '/operators/masquerade/stop',
        { escort_id: escortId },
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );

      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to stop masquerade', response.data);
      }

      return true;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Fetch the calling operator's current active masquerade session from the
   * Phase 2A session table.
   *
   * Used on page load to restore masquerade state without requiring the
   * operator to manually click "Start session" again after a browser reload.
   *
   * Returns { session: { id, escort: { id, name }, started_at, expires_at } }
   * or { session: null } when no active session exists (including when the
   * MASQUERADE_SESSIONS_ENABLED flag is off on the gateway).
   *
   * Never throws for the "no session" case — callers should only expect a
   * throw on genuine network/auth errors.
   */
  async getCurrentMasqueradeSession(authToken = null) {
    try {
      const response = await this.client.get('/operators/masquerade/current', {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      // Both "found" and "not found" are valid 200 responses.
      // Return the data object so callers can check data.session.
      return response.data.data ?? { session: null };
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Fetch the tenant's active snippet library for the operator console
   * slash-menu (Operator Console v2 — Milestone 4; trigger kinds in M2).
   * GET /operators/snippets?kind=&escort_id=
   * Returns an array of { id, kind, escort_user_id, name, body, sort_order,
   * is_active, usage_count, last_used_at }.
   *
   * Optional filters: kind ('snippet'|'trigger') narrows by kind; escortId
   * keeps shared snippets AND that escort's triggers (composer view).
   */
  async getOperatorSnippets(authToken = null, { kind = null, escortId = null } = {}) {
    try {
      const params = {};
      if (kind) params.kind = kind;
      if (escortId) params.escort_id = escortId;
      const response = await this.client.get('/operators/snippets', {
        params: Object.keys(params).length ? params : undefined,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch snippets', response.data);
      }
      return response.data.data.snippets || [];
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * One random ACTIVE trigger (per-escort conversation opener) for the
   * composer dice button / "/trigger" slash command (M2).
   * GET /operators/snippets/trigger-random?escort_id=&exclude_id=
   *
   * excludeId skips the last-used trigger for the conversation; the gateway
   * falls back to the excluded one when it is the only trigger. Returns the
   * snippet object, or null when the escort has no triggers (404).
   */
  async getRandomTrigger(authToken = null, { escortId, excludeId = null } = {}) {
    try {
      const params = { escort_id: escortId };
      if (excludeId) params.exclude_id = excludeId;
      const response = await this.client.get('/operators/snippets/trigger-random', {
        params,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch trigger', response.data);
      }
      return response.data.data.snippet || null;
    } catch (error) {
      const normalized = this.normalizeError(error);
      // "No triggers for this escort" is a valid empty outcome, not an error.
      if (normalized?.status === 404) return null;
      throw normalized;
    }
  }

  /**
   * Operator-managed profile online/offline override (M2).
   * PUT /operators/escorts/{escortId}/presence { online, duration }
   *
   * duration: '1h' | '4h' | '8h' | null (until manually disabled).
   * Returns { presence_override: { online, expires_at } | null }.
   */
  async setOperatorEscortPresence(authToken, escortId, { online, duration = null } = {}) {
    try {
      const payload = { online: !!online };
      if (online && duration) payload.duration = duration;
      const response = await this.client.put(
        `/operators/escorts/${escortId}/presence`,
        payload,
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to update presence', response.data);
      }
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Fetch the shared operator notes for a conversation (Milestone 4).
   * GET /operators/conversations/{id}/notes
   * Returns an array of { id, body, operator: { id, name }, created_at }.
   */
  async getConversationNotes(authToken, conversationId) {
    try {
      const response = await this.client.get(
        `/operators/conversations/${conversationId}/notes`,
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to fetch notes', response.data);
      }
      return response.data.data.notes || [];
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Add a shared operator note to a conversation (Milestone 4).
   * POST /operators/conversations/{id}/notes
   * Returns the created note ({ id, body, operator, created_at }).
   */
  async addConversationNote(authToken, conversationId, body) {
    try {
      const response = await this.client.post(
        `/operators/conversations/${conversationId}/notes`,
        { body },
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined }
      );
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to add note', response.data);
      }
      return response.data.data.note;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Rooms & LiveKit
   * ==================================================================== */

  async getRooms() {
    const cacheKey = 'rooms';
    const cached = this.getFromCache(cacheKey, 2 * 60 * 1000); // 2 minutes cache
    if (cached) return cached;

    try {
      const response = await this.client.get('/rooms');
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get rooms', response.data);
      }
      
      const rooms = response.data.data;
      this.setInCache(cacheKey, rooms);
      
      return rooms;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getRoom(id) {
    try {
      const response = await this.client.get(`/rooms/${id}`);
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get room', response.data);
      }
      
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async createRoom(roomData) {
    try {
      const response = await this.client.post('/rooms', roomData);
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to create room', response.data);
      }
      
      // Clear rooms cache since we added a new one
      this.removeFromCache('rooms');
      
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async generateRoomToken(roomId, participantName = null) {
    try {
      const payload = {};
      if (participantName) {
        payload.participant_name = participantName;
      }

      const response = await this.client.post(`/rooms/${roomId}/join-token`, payload);
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to generate token', response.data);
      }
      
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async sendRoomTip(roomId, tipData) {
    try {
      const response = await this.client.post(`/rooms/${roomId}/tip`, tipData);
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to send tip', response.data);
      }
      
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Wallet & Billing
   * ==================================================================== */

  async getWalletBalance() {
    const cacheKey = 'wallet_balance';
    const cached = this.getFromCache(cacheKey, 1 * 60 * 1000); // 1 minute cache
    if (cached) return cached;

    try {
      const response = await this.client.get('/wallet/balance?view=all');
      
      if (!response.data.status) {
        throw new FrontendServiceError(response.data.message || 'Failed to get balance', response.data);
      }
      
      const balance = response.data.data.balances;
      this.setInCache(cacheKey, balance);
      
      return balance;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Top-up packages (slice J.2)
   * ====================================================================
   *
   * Read-only catalog of wallet top-up offers. Backed by
   * GET /api/v1/payments/topup-packages which resolves the most-specific
   * (frontend_project, tenant) row per code with platform-default
   * fallback. The payload shape per package is documented in the
   * controller and asserted by tests — the FE only treats it as a
   * read-only DTO.
   */

  /**
   * Fetch the active top-up packages for the current tenant + frontend
   * project. Cached for 5 minutes (catalog rarely changes intra-session).
   *
   * @returns {Promise<{ currency: string, packages: Array, frontend_project: any, tenant: any }>}
   */
  async listTopUpPackages() {
    const cacheKey = 'topup_packages';
    const cached   = this.getFromCache(cacheKey, 5 * 60 * 1000);
    if (cached) return cached;

    try {
      const response = await this.client.get('/payments/topup-packages');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to list top-up packages',
          response.data
        );
      }
      const data = response.data.data || {};
      this.setInCache(cacheKey, data);
      return data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Available payment providers for the current tenant. Cached for 5
   * minutes so the InlineTopUp sheet doesn't re-fetch on every open.
   *
   * @returns {Promise<{ items: Array<{ id:number, name:string }> }>}
   */
  async listPaymentProviders() {
    const cacheKey = 'payment_providers';
    const cached   = this.getFromCache(cacheKey, 5 * 60 * 1000);
    if (cached) return cached;

    try {
      const response = await this.client.get('/payments/providers');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to list payment providers',
          response.data
        );
      }
      const data = response.data.data || {};
      this.setInCache(cacheKey, data);
      return data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Payment methods grouped by provider. Cached briefly so the InlineTopUp
   * sheet can pre-resolve provider + method without UI delay.
   *
   * @returns {Promise<{ items: Array<{ provider_id:number, provider_name:string, methods:Array }> }>}
   */
  async listPaymentMethods() {
    const cacheKey = 'payment_methods';
    const cached   = this.getFromCache(cacheKey, 5 * 60 * 1000);
    if (cached) return cached;

    try {
      const response = await this.client.get('/payments/methods');
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to list payment methods',
          response.data
        );
      }
      const data = response.data.data || {};
      this.setInCache(cacheKey, data);
      return data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /**
   * Create a one-time payment via POST /payments/one-time.
   *
   * Slice J.2: the preferred shape uses `package_code` so the gateway
   * resolves amount / credits_cents / bonus_cents / wallet_type server-side.
   * idempotency_key is REQUIRED on the package path. Returns the standard
   * Gateway envelope `data` (payment_id, checkout_url, status,
   * idempotent_replay, package).
   *
   * @param {object} payload
   * @param {string} payload.provider           PSP name returned by `GET /payments/providers`
   *                                            (e.g. 'mollie', 'nowpayments',
   *                                            'icepay_xplow', 'icepay_leadcenter').
   * @param {string} payload.package_code       e.g. 'popular_75'
   * @param {string} payload.idempotency_key    stable replay key
   * @param {string} payload.redirect_url       caller-supplied URL
   * @param {number=} payload.payment_method_id
   * @param {string=} payload.method            method code (e.g. 'ideal', 'bancontact')
   *
   * @returns {Promise<{payment_id:string, checkout_url:string|null, status:string, idempotent_replay:boolean, package?:any}>}
   */
  async createOneTimePayment(payload) {
    try {
      const response = await this.client.post('/payments/one-time', payload);
      if (!response.data.status) {
        throw new FrontendServiceError(
          response.data.message || 'Failed to create payment',
          response.data
        );
      }
      // Bust any cached wallet balance so the chip refreshes after the
      // user returns from the PSP.
      this.removeFromCache?.('wallet_balance');
      return response.data.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  /* ====================================================================
   * Cache Management
   * ==================================================================== */

  setInCache(key, value, ttl = null) {
    const expireTime = Date.now() + (ttl || this.cacheTimeout);
    this.cache.set(key, { value, expireTime });
  }

  getFromCache(key, maxAge = null) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    const age = maxAge || this.cacheTimeout;
    
    if (now > cached.expireTime || (maxAge && now - (cached.expireTime - this.cacheTimeout) > age)) {
      this.cache.delete(key);
      return null;
    }

    return cached.value;
  }

  removeFromCache(key) {
    this.cache.delete(key);
  }

  clearCache() {
    this.cache.clear();
  }

  /* ====================================================================
   * Storage Management
   * ==================================================================== */

  setAuthToken(token) {
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth_token', token);
    }
    // A freshly issued token means a new session has begun, so we are no
    // longer in a logout teardown — clear the guard so future 401s are
    // handled normally again.
    this.isLoggingOut = false;
  }

  getAuthToken() {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('auth_token');
    }
    return null;
  }

  clearAuthToken() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token');
    }
  }

  setUserData(user) {
    if (typeof window !== 'undefined') {
      localStorage.setItem('user_data', JSON.stringify(user));
    }
  }

  getUserData() {
    if (typeof window !== 'undefined') {
      const userData = localStorage.getItem('user_data');
      return userData ? JSON.parse(userData) : null;
    }
    return null;
  }

  clearUserData() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('user_data');
    }
  }

  setTenantData(tenant) {
    persistTenantData(tenant);
  }

  getTenantData() {
    return readPersistedTenantData();
  }

  setLogoutRedirectUri(uri) {
    persistLogoutRedirectUri(uri);
  }

  getLogoutRedirectUri() {
    return readLogoutRedirectUri();
  }

  /* ====================================================================
   * Utility Methods
   * ==================================================================== */

  isAuthenticated() {
    return !!this.getAuthToken();
  }

  generateRequestId() {
    return 'req_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  handleUnauthorized() {
    const currentToken = this.getAuthToken();
    if (currentToken) {
      console.log('🔑 Token invalid, clearing auth');
      this.clearAuthToken();
      this.clearUserData();
      this.clearCache();
      
      // Only redirect if we're not already on a login page
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
  }

  normalizeError(error) {
    if (error instanceof FrontendServiceError) {
      return error;
    }

    if (error.response?.data) {
      const { message, data, status } = error.response.data;
      return new FrontendServiceError(
        message || error.message || 'An error occurred',
        data || {},
        error.response.status
      );
    }

    return new FrontendServiceError(error.message || 'An error occurred', {});
  }

  /**
   * Variant of normalizeError for gateway endpoints that use flat JSON
   * envelopes (`{ error: '…' }` on failure instead of the standard
   * `{ status, message, data }`): /reports, /me/restrictions. Prefers the
   * flat `error` string so the UI surfaces the server's actual reason.
   */
  normalizeFlatError(error) {
    const flat = error?.response?.data;
    if (flat && typeof flat.error === 'string' && flat.error) {
      return new FrontendServiceError(flat.error, flat, error.response.status);
    }
    return this.normalizeError(error);
  }
}

/**
 * Custom error class for Frontend Service errors
 */
class FrontendServiceError extends Error {
  constructor(message, data = {}, status = null) {
    super(message);
    this.name = 'FrontendServiceError';
    this.data = data;
    this.status = status;
  }
}

// Create singleton instance
const frontendService = new FrontendService();

export default frontendService;
export { FrontendServiceError };